import type { ElementType } from "react"

import { ArrowIcon, Grid, Heading, Reel, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"

interface World {
  id: string
  title: string
  category?: string
  image: string
  href?: string
}

// Caption below the photograph, not on it — the two-up collection card is the one
// place the reference lets the type leave the image, because the photo is doing the
// selling and the label is only saying which door this is.
const WorldCard = ({ world, priority }: { world: World; priority?: boolean }) => {
  const Card: ElementType = world.href ? Link : "div"
  return (
    <Card
      {...(world.href ? { href: world.href } : {})}
      className="press group focus-visible:ring-brand rounded-20x block min-w-0 focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none"
    >
      <span className="rounded-20x bg-surface-muted aspect-tile relative block overflow-hidden">
        <TileImage
          src={world.image}
          alt=""
          fill
          sizes="(max-width: 768px) 100vw, 60vw"
          priority={priority}
          className="duration-slow ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
        />
        {world.href && (
          <span
            aria-hidden
            className="bg-surface-paper text-fg-on-paper duration-quick ease-productive absolute right-300 bottom-300 grid size-11 place-items-center rounded-full shadow-md transition-transform motion-safe:group-hover:scale-105 @2xl:right-400 @2xl:bottom-400 @2xl:size-13"
          >
            <ArrowIcon size={19} />
          </span>
        )}
      </span>
      <span className="mt-300 flex flex-wrap items-baseline gap-x-250 gap-y-100">
        <Heading as="span" variant="lg" family="display" tone="default" className="@2xl:text-heading-xl font-bold tracking-tight">
          {world.title}
        </Heading>
        {world.category && (
          <Text as="span" variant="bodySm" family="text" tone="muted">
            {world.category}
          </Text>
        )}
      </span>
    </Card>
  )
}

export const WorldCards = ({ items }: { items: World[] }) => (
  <div className="@container">
    <Reel gap="300" snap inset className="@2xl:hidden">
      {items.map((world, index) => (
        <div key={world.id} className="w-4/5 shrink-0 snap-start">
          <WorldCard world={world} priority={index === 0} />
        </div>
      ))}
    </Reel>
    <Grid cols={2} gap="400" className="px-gut hidden @2xl:grid">
      {items.map((world, index) => (
        <WorldCard key={world.id} world={world} priority={index === 0} />
      ))}
    </Grid>
  </div>
)
